import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { StatCounter } from "@/components/StatCounter";
import { CTASection } from "@/components/CTASection";
import { STATS, IMAGES, NAP } from "@/data/site";
import { ShieldCheck, MapPin, Buildings, Code } from "@phosphor-icons/react";
import { useSEO, breadcrumbLd, organizationLd, localBusinessLd } from "@/lib/seo";

const VALUES = [
  { Icon: ShieldCheck, t: "Licensed & insured", d: "Fully licensed and insured for residential and commercial work in Alberta." },
  { Icon: MapPin, t: "Calgary roots", d: "Local crews who know Calgary codes, climate, neighbourhoods and inspectors." },
  { Icon: Buildings, t: "Structure", d: "Decades of combined construction craft — homes, renos and commercial." },
  { Icon: Code, t: "Signal", d: "An in-house digital studio: branding, web, marketing and applied AI." },
];

export default function About() {
  useSEO({
    title: "About MRE Developments | Calgary Builders + Digital",
    description:
      "MRE Developments is a Calgary firm that builds physical spaces and the digital presence that grows them. Licensed, insured, local and tech-forward.",
    path: "/about",
    image: IMAGES.realFullReno,
    jsonLd: [
      organizationLd(),
      localBusinessLd(),
      breadcrumbLd([{ name: "Home", path: "/" }, { name: "About", path: "/about" }]),
    ],
  });

  return (
    <div data-testid="about-page">
      <PageHero
        overline="About"
        title="One partner. Structure and signal."
        intro="MRE Developments builds custom homes, renovations and commercial spaces across Calgary — and the brand, web and AI that grow the businesses inside them. Quality craftsmanship, exceptional service, one accountable team."
        image={IMAGES.realFullReno}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "About", path: "/about" }]}
      />

      {/* Story */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <SectionHeading
            overline="Our story"
            title="Your trusted partner in Calgary construction."
            intro="At MRE Developments, we specialize in custom home construction, renovations and commercial projects across Calgary. With a focus on quality craftsmanship and exceptional customer service, we're committed to bringing your vision to life."
          />
          <p className="mt-6 text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Our mission</strong> is to deliver high-quality construction
            that exceeds expectations — on time, within budget, and to the highest standards of craft. We believe
            communication is the foundation of every successful project, so we work closely with you from the first
            consultation to final handover.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Our approach</strong> pairs decades of construction craft with an
            in-house design studio — branding, web, marketing and applied AI. Premium work at an honest, medium-tier
            price. No jargon. No surprises. Just long-lasting relationships built on trust.
          </p>
        </div>
        <div className="lg:col-span-6">
          <div className="relative border border-border overflow-hidden">
            <img src={IMAGES.realNewHome} alt="MRE Developments new home construction in Calgary" loading="lazy" className="w-full aspect-[4/3] object-cover grayscale" />
            <div className="absolute inset-0 blueprint-grid-fine opacity-30" />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="border-y border-border bg-secondary/20">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-28">
          <SectionHeading overline="What we stand on" title="Built on four things." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 mt-12 border-t border-l border-border">
            {VALUES.map((v, i) => (
              <Reveal key={v.t} delay={i * 80} className="border-b border-r border-border p-8">
                <v.Icon size={32} weight="light" className="text-primary" />
                <h3 className="font-display uppercase tracking-tight text-2xl mt-4">{v.t}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{v.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 grid grid-cols-2 lg:grid-cols-4 gap-10">
        {STATS.map((s) => (
          <StatCounter key={s.label} {...s} />
        ))}
      </section>

      {/* Service area */}
      <section className="border-t border-border">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-28">
          <SectionHeading overline="Service area" title="Calgary & region." intro="Proudly serving Calgary and the surrounding communities." />
          <div className="flex flex-wrap gap-3 mt-10">
            {NAP.areaServed.map((a) => (
              <span key={a} className="font-mono-tech text-xs tracking-widest uppercase border border-border px-4 h-11 inline-flex items-center text-foreground">
                {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      <CTASection title="Let's build it together." />
    </div>
  );
}
